import React, { memo, useRef } from 'react';
import { geoPath, geoNaturalEarth1 } from 'd3-geo';
import { SizeMe } from 'react-sizeme';
import clsx from 'clsx';
import { feature as topojsonFeature } from 'topojson-client';
import Tooltip, { positionTooltip } from '@reach/tooltip';

import world from 'sane-topojson/dist/world_110m.json';

import './styles.scss';

const countries = topojsonFeature(world, world.objects.countries);
countries.features = countries.features.filter((feature) => feature.id && feature.id !== 'ATA');
countries.features.forEach((feature) => {
  // Separate French Guiana from France
  if (feature.id === 'FRA') {
    const newFeature = {
      type: 'Feature',
      id: 'FRA-X',
      geometry: {
        coordinates: feature.geometry.coordinates[0],
        type: 'Polygon',
      },
    };
    feature.geometry.coordinates = feature.geometry.coordinates.slice(1);
    countries.features.push(newFeature);
  }

  // Separate territories from USA
  // if (feature.id === 'USA') {
  //   const newFeature = {
  //     type: 'Feature',
  //     id: 'USA-X',
  //     geometry: {
  //       coordinates: feature.geometry.coordinates.slice(1),
  //       type: 'MultiPolygon',
  //     },
  //   };
  //   feature.geometry.coordinates = feature.geometry.coordinates.slice(0, 1);
  //   countries.features.push(newFeature);
  // }
});

const PROJECTION_ROTATION = [-11.5, 0, 0];

function largestPolygonGeometry(geometry) {
  if (!geometry || geometry.type !== 'MultiPolygon') {
    return geometry;
  }
  const largest = geometry.coordinates.reduce(
    (best, coords) => (coords[0] && coords[0].length > best[0].length ? coords : best),
    [[]]
  );
  return { type: 'Polygon', coordinates: largest };
}

export function WorldMap({ data, width = 512, height = 512 }) {
  const rootRef = useRef();
  const projection = geoNaturalEarth1()
    .fitSize([width, height], countries)
    .rotate(PROJECTION_ROTATION);
  const path = geoPath().projection(projection);

  return (
    <>
      <svg ref={rootRef} className="world-map" viewBox={`0 0 ${width} ${height}`}>
        <g>
          {countries.features.map((country) => {
            const id = country.id || country.properties.iso_a3;
            const datum = data[id];
            const available = !!datum;
            const child = (
              <path data-iso={id} d={path(country.geometry)} data-available={available} />
            );
            const hitareaFeature = {
              type: 'Feature',
              geometry: largestPolygonGeometry(country.geometry),
            };
            const tooltipProjectedBbox = hitareaFeature ? path.bounds(hitareaFeature) : undefined;

            return datum && datum.url ? (
              <Tooltip
                label={<TooltipContent title={datum.title} iconUrl={datum.iconUrl} />}
                position={(triggerRect, tooltipRect) => {
                  const rootRect = rootRef.current.getBoundingClientRect();
                  const position = {
                    top: rootRect.top + tooltipProjectedBbox[1][1] + window.scrollY,
                    left:
                      rootRect.left +
                      tooltipProjectedBbox[0][0] +
                      (tooltipProjectedBbox[1][0] - tooltipProjectedBbox[0][0]) / 2 -
                      tooltipRect.width / 2,
                  };
                  return position;
                }}
              >
                <a
                  key={id}
                  href={datum.url}
                  className={clsx(
                    'world-map__country',
                    available && 'world-map__country--available'
                  )}
                >
                  {child}
                </a>
              </Tooltip>
            ) : (
              <g
                key={id}
                className={clsx('world-map__country', available && 'world-map__country--available')}
              >
                {child}
              </g>
            );
          })}
        </g>
      </svg>
    </>
  );
}

function TooltipContent({ title, iconUrl }) {
  return (
    <div className="world-map-tooltip">
      {iconUrl ? (
        <div className="world-map-tooltip__flag">
          <img src={iconUrl} />
        </div>
      ) : null}
      <div className="world-map-tooltip__name">{title}</div>
    </div>
  );
}

function _SizedWorldMap(props) {
  return (
    <SizeMe monitorHeight>
      {({ size }) => (
        <div className="sized-world-map">
          {size.width && size.height ? (
            <WorldMap width={size.width} height={size.height} {...props} />
          ) : null}
        </div>
      )}
    </SizeMe>
  );
}

export const SizedWorldMap = memo(_SizedWorldMap);

export default memo(WorldMap);
